import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const ThemeContext = createContext({ theme: "light", suit: "classic", setTheme: () => {}, toggle: () => {}, setSuit: () => {} });

const THEME_KEY = "ledgerly-theme";
const SUIT_KEY = "ledgerly-suit";
const VALID_SUITS = ["classic", "symbiote", "miles"];

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || getSystemTheme();
  });
  const [suit, setSuitState] = useState(() => {
    if (typeof window === "undefined") return "classic";
    const s = localStorage.getItem(SUIT_KEY);
    return VALID_SUITS.includes(s) ? s : "classic";
  });
  const isInitialSuit = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  // On suit change: apply data-suit, add smooth colour transition, and fire a one-shot intro flash
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-suit", suit);
    if (isInitialSuit.current) {
      isInitialSuit.current = false;
      return;
    }
    // 1. Smooth 400ms colour transition across all themed properties
    root.classList.add("suit-transitioning");
    const cleanupTransition = setTimeout(() => root.classList.remove("suit-transitioning"), 500);
    // 2. Signature intro flash overlay (Classic web ripple / Symbiote ink drip / Miles electric arc)
    const overlay = document.createElement("div");
    overlay.className = `suit-flash ${suit}`;
    overlay.setAttribute("aria-hidden", "true");
    document.body.appendChild(overlay);
    const cleanupOverlay = setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 750);
    return () => {
      clearTimeout(cleanupTransition);
      clearTimeout(cleanupOverlay);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
  }, [suit]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      if (!localStorage.getItem(THEME_KEY)) setThemeState(e.matches ? "dark" : "light");
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const setTheme = (v) => {
    try { localStorage.setItem(THEME_KEY, v); } catch (e) { /* ignore */ }
    setThemeState(v);
  };
  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");
  const setSuit = (v) => {
    const next = VALID_SUITS.includes(v) ? v : "classic";
    try { localStorage.setItem(SUIT_KEY, next); } catch (e) { /* ignore */ }
    setSuitState(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, suit, setTheme, toggle, setSuit }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
